import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

import Alert from './ui/Alert';
import Badge from './ui/Badge';
import Button from './ui/Button';
import Card from './ui/Card';
import EmptyState from './ui/EmptyState';
import SectionHeader from './ui/SectionHeader';
import Spinner from './ui/Spinner';

function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('omschrijving');

  useEffect(() => {
    let isMounted = true;
    setError('');
    setProduct(null);
    setLoading(true);

    fetch(`/api/products?id=${encodeURIComponent(id)}&details=1`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(`API error ${res.status}: ${body || res.statusText}`);
        }
        return res.json();
      })
      .then(data => {
        if (!isMounted) return;
        setProduct(data || null);
        setActiveTab('omschrijving');
        setLoading(false);
      })
      .catch((e) => {
        if (!isMounted) return;
        setError(e?.message || 'Failed to fetch');
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [id]);

  if (error) {
    return (
      <div>
        <SectionHeader title="Product" subtitle={`ID: ${id}`} />
        <Alert tone="danger" title="Kon product niet laden">
          {error}
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <Card className="pageCard" style={{ display: 'flex', justifyContent: 'center' }}>
        <Spinner label="Product laden…" />
      </Card>
    );
  }

  if (!product) {
    return (
      <EmptyState
        title="Product niet gevonden"
        description="Dit product bestaat niet (meer) of kon niet worden opgehaald."
      >
        <div style={{ marginTop: 14 }}>
          <Link className="link" to="/products">
            ← Terug naar overzicht
          </Link>
        </div>
      </EmptyState>
    );
  }

  const techParts = String(product.tech || '')
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);

  const tabButton = (key, label) => (
    <button
      type="button"
      className={`tabButton ${activeTab === key ? 'tabButton--active' : ''}`}
      onClick={() => setActiveTab(key)}
    >
      {label}
    </button>
  );

  const showHtmlOrFallback = (html, fallbackText) => {
    const safe = String(html || '').trim();
    if (safe) {
      return <div className="richText" dangerouslySetInnerHTML={{ __html: safe }} />;
    }
    const t = String(fallbackText || '').trim();
    if (t) {
      return <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text1)' }}>{t}</div>;
    }
    return (
      <EmptyState
        title="Geen informatie beschikbaar"
        description="PremTech levert voor dit product (nog) geen inhoud in deze sectie."
      />
    );
  };

  return (
    <div>
      <SectionHeader
        title={product.name}
        subtitle={product.categories?.length ? product.categories.map((c) => c.name).join(' • ') : `ID: ${product.id}`}
        right={
          <Link className="link" to="/products">
            ← Terug naar overzicht
          </Link>
        }
      />

      <Card className="pageCard">
        <div className="twoCol">
          <div>
            <div className="detailMedia">
              <img
                src={product.image || '/placeholder.svg'}
                alt={product.name}
                onError={(e) => {
                  e.currentTarget.src = '/placeholder.svg';
                }}
              />
            </div>

            <div className="detailBadges">
              {(product.categories || []).slice(0, 6).map((c) => (
                <Badge key={c.id} tone={['Afdichten', 'Verbinden', 'Reinigen', 'Handen', 'Beschermen', 'Smeren', 'Gereedschappen'].includes(c.name) ? 'brand' : 'neutral'}>
                  {c.name}
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <div className="tabsRow">
              {tabButton('omschrijving', 'Omschrijving')}
              {tabButton('technisch', 'Technische info')}
              {tabButton('praktisch', 'Praktische info')}
              {tabButton('eigenschappen', 'Eigenschappen')}
              {tabButton('documenten', 'Documenten')}
            </div>

            <div className="tabPanel">
              {activeTab === 'omschrijving'
                ? showHtmlOrFallback(product.omschrijvingHtml, product.omschrijvingText || product.description)
                : null}

              {activeTab === 'technisch' ? (
                product.attributes?.length ? (
                  <div>
                    <div className="kvs" style={{ marginBottom: 16 }}>
                      <div className="kv">
                        <div className="kv__k">Technische info</div>
                        <div className="kv__v">
                          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text1)' }}>
                            {techParts.length
                              ? techParts.map((t) => <li key={t}>{t}</li>)
                              : null}
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                      Alle specificaties
                    </div>
                    <div className="kvs">
                      {product.attributes.map((a) => (
                        <div className="kv" key={a.name}>
                          <div className="kv__k">{a.name}</div>
                          <div className="kv__v">{(a.options || []).join(', ') || '—'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  showHtmlOrFallback('', product.technischeInfoText)
                )
              ) : null}

              {activeTab === 'praktisch'
                ? showHtmlOrFallback(product.praktischeInfoHtml, product.praktischeInfoText)
                : null}

              {activeTab === 'eigenschappen'
                ? showHtmlOrFallback(product.eigenschappenHtml, product.eigenschappenText)
                : null}

              {activeTab === 'documenten' ? (
                product.documenten?.length ? (
                  <div>
                    <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                      Downloads
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {product.documenten.map((d) => (
                        <li key={d.href}>
                          <a className="link" href={d.href} target="_blank" rel="noreferrer">
                            {d.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <EmptyState
                    title="Geen documenten"
                    description="Voor dit product zijn geen documenten gevonden."
                  />
                )
              ) : null}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 }}>
              {product.permalink ? (
                <Button as="a" href={product.permalink} target="_blank" rel="noreferrer" variant="primary">
                  Open op premtech.nl
                </Button>
              ) : null}
              <Button as={Link} to="/products" variant="ghost">
                Terug
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default ProductDetail;
